import { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useReadContracts } from 'wagmi';
import type { Address, Hex } from 'viem';
import {
  usePendingSafeTransactions,
  fetchExecutedTransactions,
  filterTimelockTransactions,
  SAFE_TX_SERVICE_SHORTNAMES,
  type SafeTransaction,
} from './usePendingSafeTransactions';
import { useMinDelay } from './useTimelockStatus';
import {
  decodeTimelockCalldata,
  TIMELOCK_ABI,
  type DecodedTimelock,
} from '../lib/timelock';
import {
  deriveTimelockStatusFromTimestamp,
  isDisplayableScheduledStatus,
  type TimelockOperationStatus,
} from '../lib/timelock-status';
import { type SupportedChainId } from '../config/wagmi';

// Cache durations
const EXECUTED_TX_STALE_TIME = 10 * 60 * 1000; // 10 minutes
const EXECUTED_TX_GC_TIME = 30 * 60 * 1000; // 30 minutes

export interface ScheduledOperation {
  safeTxHash: string;
  nonce: number;
  timelockCalldata: Hex;
  decoded: DecodedTimelock;
  operationId: Hex;
  submissionDate: string;
  safeStatus: 'pending' | 'executed';
  confirmations: number;
  confirmationsRequired: number;
  timelockStatus?: TimelockOperationStatus;
}

export type ScheduledOperationLookupState =
  | 'not_found'
  | 'pending_source_unavailable'
  | 'executable'
  | 'not_executable'
  | 'unknown_status';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const ONE_HUNDRED_EIGHTY_DAYS_MS = 180 * 24 * 60 * 60 * 1000;

type DecodedScheduledSource = {
  tx: SafeTransaction;
  timelockCalldata: Hex;
  decoded: DecodedTimelock;
};

function decodeScheduledOperations(
  transactions: SafeTransaction[] | undefined,
  timelockAddress: Address | undefined
): DecodedScheduledSource[] {
  if (!transactions || !timelockAddress) return [];

  const decodedOps: DecodedScheduledSource[] = [];
  const filtered = filterTimelockTransactions(transactions, timelockAddress);

  for (const tx of filtered) {
    try {
      const decoded = decodeTimelockCalldata(tx.timelockCalldata);
      if (decoded && (decoded.functionName === 'schedule' || decoded.functionName === 'scheduleBatch')) {
        decodedOps.push({ tx, timelockCalldata: tx.timelockCalldata, decoded });
      }
    } catch {
      // Skip transactions that can't be decoded.
    }
  }

  return decodedOps;
}

function buildScheduledOperation(
  source: DecodedScheduledSource,
  safeStatus: 'pending' | 'executed',
  timelockStatus?: TimelockOperationStatus
): ScheduledOperation | null {
  if (!source.decoded.operationId) return null;

  return {
    safeTxHash: source.tx.safeTxHash,
    nonce: source.tx.nonce,
    timelockCalldata: source.timelockCalldata,
    decoded: source.decoded,
    operationId: source.decoded.operationId,
    submissionDate: source.tx.submissionDate,
    safeStatus,
    confirmations: source.tx.confirmations?.length ?? 0,
    confirmationsRequired: source.tx.confirmationsRequired,
    timelockStatus,
  };
}

export function useScheduledOperations(
  safeAddress: Address | undefined,
  chainId: number | undefined,
  timelockAddress: Address | undefined
) {
  const supportedChainId = chainId as SupportedChainId | undefined;

  // Get minDelay to determine lookback period
  const { minDelay } = useMinDelay(timelockAddress, supportedChainId);

  // Fetch pending Safe transactions
  const {
    data: pendingTxs,
    isLoading: pendingLoading,
    bootstrapLoading: pendingBootstrapLoading,
    error: pendingError,
    bootstrapError: pendingBootstrapError,
    refetch: refetchPending,
  } = usePendingSafeTransactions(safeAddress, chainId);

  // Calculate lookback date based on minDelay
  const sinceDate = useMemo(() => {
    if (minDelay !== undefined) {
      // Keep at least 6 months of history so long-lived ready ops still appear.
      const lookbackMs = Math.max(Number(minDelay) * 1000 + THIRTY_DAYS_MS, ONE_HUNDRED_EIGHTY_DAYS_MS);
      return new Date(Date.now() - lookbackMs);
    }
    // Default to 6 months if minDelay not available
    return new Date(Date.now() - ONE_HUNDRED_EIGHTY_DAYS_MS);
  }, [minDelay]);

  // Fetch executed Safe transactions
  const {
    data: executedTxs,
    isLoading: executedLoading,
    error: executedError,
    refetch: refetchExecuted,
  } = useQuery({
    queryKey: ['executedSafeTransactions', safeAddress, chainId, sinceDate.getTime()],
    queryFn: () => fetchExecutedTransactions(safeAddress!, chainId!, sinceDate),
    enabled: !!safeAddress && !!chainId && !!SAFE_TX_SERVICE_SHORTNAMES[chainId],
    staleTime: EXECUTED_TX_STALE_TIME,
    gcTime: EXECUTED_TX_GC_TIME,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('429')) return false;
      return failureCount < 2;
    },
  });

  // Filter and decode schedule operations from both sources
  const { pendingScheduleOps, executedScheduleOps } = useMemo(() => {
    return {
      pendingScheduleOps: decodeScheduledOperations(pendingTxs, timelockAddress),
      executedScheduleOps: decodeScheduledOperations(executedTxs, timelockAddress),
    };
  }, [pendingTxs, executedTxs, timelockAddress]);

  // Collect operation IDs for batch status check
  const executedOperationIds = useMemo(() => {
    return executedScheduleOps
      .map((op) => op.decoded.operationId)
      .filter((id): id is Hex => !!id);
  }, [executedScheduleOps]);

  // Batch query on-chain timestamps for executed operations
  const statusContracts = useMemo(() => {
    if (!timelockAddress || executedOperationIds.length === 0) return [];

    const contracts: Array<{
      chainId?: SupportedChainId;
      address: Address;
      abi: typeof TIMELOCK_ABI;
      functionName: string;
      args: [Hex];
    }> = [];

    for (const operationId of executedOperationIds) {
      contracts.push({
        chainId: supportedChainId,
        address: timelockAddress,
        abi: TIMELOCK_ABI,
        functionName: 'getTimestamp',
        args: [operationId],
      });
    }

    return contracts;
  }, [timelockAddress, executedOperationIds]);

  const {
    data: statusData,
    isLoading: statusLoading,
    error: statusError,
  } = useReadContracts({
    contracts: statusContracts,
    batchSize: 1024,
    query: {
      enabled: statusContracts.length > 0,
      refetchOnWindowFocus: false,
    },
  });

  // Parse status data into a map
  const statusMap = useMemo(() => {
    const map = new Map<Hex, TimelockOperationStatus>();

    if (!statusData || statusData.length === 0) return map;
    const now = BigInt(Math.floor(Date.now() / 1000));

    for (let i = 0; i < executedOperationIds.length; i++) {
      const operationId = executedOperationIds[i];
      const entry = statusData[i];
      if (!entry || entry.status === 'failure') continue;

      const timestamp = entry.result;
      if (typeof timestamp !== 'bigint') continue;

      map.set(operationId, deriveTimelockStatusFromTimestamp(timestamp, now));
    }

    return map;
  }, [statusData, executedOperationIds]);

  const lookupOperations = useMemo(() => {
    const result: ScheduledOperation[] = [];

    for (const source of pendingScheduleOps) {
      const operation = buildScheduledOperation(source, 'pending');
      if (operation) result.push(operation);
    }

    for (const source of executedScheduleOps) {
      const operation = buildScheduledOperation(
        source,
        'executed',
        source.decoded.operationId ? statusMap.get(source.decoded.operationId) : undefined
      );
      if (operation) result.push(operation);
    }

    return result;
  }, [executedScheduleOps, pendingScheduleOps, statusMap]);

  // Build final operations list
  const operations = useMemo(() => {
    const result = lookupOperations.filter((operation) => {
      if (operation.safeStatus === 'pending') return true;
      return isDisplayableScheduledStatus(operation.timelockStatus);
    });

    // Sort: Ready first, then pending timelock, then pending signatures
    result.sort((a, b) => {
      // Ready operations first
      if (a.timelockStatus?.isReady && !b.timelockStatus?.isReady) return -1;
      if (!a.timelockStatus?.isReady && b.timelockStatus?.isReady) return 1;

      // Then pending timelock (executed but waiting)
      if (a.safeStatus === 'executed' && b.safeStatus === 'pending') return -1;
      if (a.safeStatus === 'pending' && b.safeStatus === 'executed') return 1;

      // Then by date (newest first)
      return new Date(b.submissionDate).getTime() - new Date(a.submissionDate).getTime();
    });

    return result;
  }, [lookupOperations]);

  // Combined refetch for all data
  const refetch = useCallback(() => {
    refetchPending();
    refetchExecuted();
  }, [refetchPending, refetchExecuted]);

  return {
    operations,
    lookupOperations,
    isLoading: pendingLoading || executedLoading || statusLoading,
    pendingLoading,
    pendingBootstrapLoading,
    executedLoading,
    statusLoading,
    error: pendingError || executedError,
    pendingBootstrapError,
    statusError,
    refetch,
  };
}

export function useScheduledOperationLookup(
  safeAddress: Address | undefined,
  chainId: number | undefined,
  timelockAddress: Address | undefined,
  operationId: Hex | undefined
) {
  const {
    lookupOperations,
    pendingLoading,
    pendingBootstrapLoading,
    executedLoading,
    statusLoading,
    error,
    pendingBootstrapError,
    statusError,
    refetch,
  } = useScheduledOperations(
    safeAddress,
    chainId,
    timelockAddress
  );

  const operation = useMemo(() => {
    if (!operationId) return undefined;

    return lookupOperations.find(
      (candidate) => candidate.operationId.toLowerCase() === operationId.toLowerCase()
    );
  }, [lookupOperations, operationId]);

  const lookupState = useMemo<ScheduledOperationLookupState>(() => {
    if (!operation) return 'not_found';
    if (operation.safeStatus === 'pending') return 'executable';
    if (!operation.timelockStatus) return 'unknown_status';
    return isDisplayableScheduledStatus(operation.timelockStatus) ? 'executable' : 'not_executable';
  }, [operation]);

  const isLoading = useMemo(() => {
    if (operation?.safeStatus === 'pending') return false;
    if (operation?.safeStatus === 'executed') return executedLoading || statusLoading;
    return executedLoading || statusLoading || pendingBootstrapLoading || pendingLoading;
  }, [executedLoading, operation, pendingBootstrapLoading, pendingLoading, statusLoading]);

  const resolvedLookupState = useMemo<ScheduledOperationLookupState>(() => {
    if (isLoading) return 'not_found';
    if (lookupState !== 'not_found') return lookupState;
    if (pendingBootstrapError) return 'pending_source_unavailable';
    return 'not_found';
  }, [isLoading, lookupState, pendingBootstrapError]);

  return {
    operation,
    lookupState: resolvedLookupState,
    isLoading,
    error,
    pendingBootstrapError,
    statusError,
    refetch,
  };
}
